<script setup>
  import {inject, ref, watch} from "vue";

  import ComponentUnsavedChanges from "@components/Alerts/UnsavedChanges.vue";
  import ComponentCard from "@components/Cards/Card.vue";
  import ComponentConfirmationDialog from "@components/Dialogs/ConfirmationDialog.vue";
  import ComponentInput from "@components/Inputs/Input.vue";
  import {useForm} from "@inertiajs/vue3";
  import {MedButton, MedSpinner} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "PatientsPatientAttributesNursingHomeAttribute",
  });

  const props = defineProps({
    attribute: {
      type: Object,
      default: () => ({}),
    },
    patient: {
      type: Object,
      default: () => ({}),
    },
    recentlyAdded: {
      type: Boolean,
      default: false,
    },
  });

  const broadcast = inject("$broadcast");

  const refConfirmationDialog = ref(null);

  const activeEdit = ref(props.recentlyAdded);
  const form = useForm({
    type: props.attribute.type,
    payload: {
      nursing_home_name: props.attribute?.payload?.nursing_home_name,
      nursing_home_ward: props.attribute?.payload?.nursing_home_ward,
      nursing_home_room: props.attribute?.payload?.nursing_home_room,
      nursing_home_carer: props.attribute?.payload?.nursing_home_carer,
    },
  });

  watch(
    () => props.recentlyAdded,
    (newValue) => {
      activeEdit.value = newValue;
    },
  );

  function cancelEdit() {
    // was recently added AND was NOT saved once
    if (props.recentlyAdded && !form.wasSuccessful) {
      remove();
    } else {
      form.reset();
      activeEdit.value = false;
    }
  }

  function save() {
    form.put(route("attributes.update", {patient: props.patient.id, attribute: props.attribute.id}), {
      preserveScroll: true,
      only: ["flash", "errors", "patient"],
      onSuccess: () => {
        activeEdit.value = false;
        broadcast.patient.postMessage(props.patient.id, {action: "reload.patient"});
      },
    });
  }

  function confirmRemove() {
    refConfirmationDialog.value.open();
  }

  function remove() {
    form.delete(route("attributes.destroy", {patient: props.patient.id, attribute: props.attribute.id}), {
      preserveScroll: true,
      only: ["flash", "errors", "patient"],
      onSuccess: () => {
        broadcast.patient.postMessage(props.patient.id, {action: "reload.patient"});
      },
    });
  }
</script>

<template>
  <form :data-test="getTestId('nursing-home-attribute-form')" @submit.prevent="save">
    <ComponentCard class="group relative hover:bg-gray-100" :test-id="getTestId('nursing-home-attribute-form')">
      <div class="flex items-baseline justify-between">
        <h6 class="text-xl">Heimpatient</h6>

        <MedSpinner v-if="form.processing" size="xs" :data-test="getTestId('nursing-home-attribute-spinner')" />

        <ComponentUnsavedChanges v-else-if="form.isDirty || form.recentlySuccessful" :form="form" />

        <span v-else class="text-xs text-gray-500">Aktualisiert {{ attribute.updated_at }}</span>
      </div>

      <template v-if="activeEdit">
        <p class="text-sm text-gray-600">
          Die Anschrift der Einrichtung können Sie im Bereich „Kontakte“ separat verknüpfen.
        </p>

        <p v-if="$page.props.client.license.is_pro" class="text-sm text-gray-600">
          Details zur Einrichtung werden bei geteilten Patienten nicht übermittelt.
        </p>

        <div class="mt-4 grid grid-cols-4 gap-8">
          <ComponentInput
            v-model="form.payload.nursing_home_name"
            :encrypted="true"
            class="col-span-4 md:col-span-2"
            label="Name der Pflegeeinrichtung"
            :validation="form.errors['payload.nursing_home_name']"
            :test-id="getTestId('nursing-home-attribute-form-card-nursing-home-name')"
          />

          <ComponentInput
            v-model="form.payload.nursing_home_ward"
            :encrypted="true"
            class="col-span-2 md:col-span-1"
            label="Station"
            :validation="form.errors['payload.nursing_home_ward']"
            :test-id="getTestId('nursing-home-attribute-form-card-nursing-home-ward')"
          />

          <ComponentInput
            v-model="form.payload.nursing_home_room"
            :encrypted="true"
            class="col-span-2 md:col-span-1"
            label="Zimmer"
            :validation="form.errors['payload.nursing_home_room']"
            :test-id="getTestId('nursing-home-attribute-form-card-nursing-home-room')"
          />

          <ComponentInput
            v-model="form.payload.nursing_home_carer"
            :encrypted="true"
            class="col-span-4 md:col-span-2"
            label="Zuständige Pflegefachkraft"
            :validation="form.errors['payload.nursing_home_carer']"
            :test-id="getTestId('nursing-home-attribute-form-card-nursing-home-carer')"
          />
        </div>
      </template>

      <template v-else>
        <div class="mt-0.5 min-h-4">
          <template v-if="patient.is_fake">
            <p class="text-sm text-gray-600">
              Details zur Einrichtung werden bei geteilten Patienten nicht übermittelt.
            </p>
          </template>

          <template
            v-if="
              attribute.payload?.nursing_home_name ||
              attribute.payload?.nursing_home_ward ||
              attribute.payload?.nursing_home_room ||
              attribute.payload?.nursing_home_carer
            "
          >
            <span v-if="attribute.payload?.nursing_home_name">
              <span class="text-gray-500">Name der Pflegeeinrichtung:</span>
              <span :data-encrypted="attribute.payload?.nursing_home_name" />
              <br />
            </span>

            <span v-if="attribute.payload?.nursing_home_ward">
              <span class="text-gray-500">Station:</span>
              <span :data-encrypted="attribute.payload?.nursing_home_ward" />
              &nbsp;
            </span>

            <span v-if="attribute.payload?.nursing_home_room">
              <span class="text-gray-500">Zimmer:</span>
              <span :data-encrypted="attribute.payload?.nursing_home_room" />
            </span>

            <span v-if="attribute.payload?.nursing_home_ward || attribute.payload?.nursing_home_room"><br /></span>

            <span v-if="attribute.payload?.nursing_home_carer">
              <span class="text-gray-500">Zuständige Pflegekraft:</span>
              <span :data-encrypted="attribute.payload?.nursing_home_carer" />
            </span>
          </template>
        </div>

        <div class="absolute right-2 bottom-2 flex gap-2">
          <MedButton
            aria-label="Angaben zum Heimpatienten bearbeiten"
            flat
            variant="primary"
            icon="edit"
            rounded="full"
            :data-test="getTestId('nursing-home-attribute-form-card-edit-button')"
            @click="activeEdit = true"
          />

          <MedButton
            aria-label="Angaben zum Heimpatienten löschen"
            flat
            icon="delete"
            rounded="full"
            :data-test="getTestId('nursing-home-attribute-form-card-delete-button')"
            @click="confirmRemove()"
          />
        </div>
      </template>

      <template v-if="activeEdit" #actions>
        <MedButton
          type="submit"
          variant="primary"
          :disabled="form.processing"
          :data-test="getTestId('nursing-home-attribute-form-card-save-button')"
        >
          Speichern
        </MedButton>

        <MedButton
          :disabled="form.processing"
          :data-test="getTestId('nursing-home-attribute-form-card-cancel-edit-button')"
          @click="cancelEdit()"
        >
          Abbrechen
        </MedButton>
      </template>
    </ComponentCard>
  </form>

  <ComponentConfirmationDialog
    ref="refConfirmationDialog"
    title="Sind Sie sicher?"
    content="Die Angabe „Heimpatient“ wird gelöscht."
    @confirmed="remove()"
  />
</template>
